'use client';

import { useState, useEffect } from 'react';
import { knowledgeBaseApi, type UserAnalytics } from '../_lib/api';
import { Loader2, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import Link from 'next/link';
import { useTranslation } from '@/hooks/useTranslation';

interface KBAnalyticsPanelProps {
  className?: string;
}

type ViewType = 'content' | 'project';

export function KBAnalyticsPanel({ className = '' }: KBAnalyticsPanelProps) {
  const { t, locale } = useTranslation();
  const [analytics, setAnalytics] = useState<UserAnalytics | null>(null);
  const [viewType, setViewType] = useState<ViewType>('content');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await knowledgeBaseApi.getUserAnalytics();
        // 使用真实数据，即使为空也显示零值而非 mock 数据
        setAnalytics(data);
      } catch (err) {
        console.error('Failed to fetch analytics:', err);
        // API 错误时显示默认零值，不使用 mock 数据
        setAnalytics({
          createdArticles: { draft: 0, published: 0 },
          views: 0,
          reads: 0,
          likes: 0,
          dislikes: 0,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className={`rounded-xl border border-gray-200 bg-white shadow-sm ${className}`}>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  const totalArticles =
    (analytics?.createdArticles.draft ?? 0) + (analytics?.createdArticles.published ?? 0);
  const formatNumber = (value: number) => new Intl.NumberFormat(locale).format(value);

  return (
    <div className={`rounded-xl border border-gray-200 bg-white shadow-sm ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-gray-900">{t.knowledgeBase.analytics.title}</h2>
          <Link href="/knowledge-base/analytics" className="text-xs text-blue-600 hover:underline">
            {t.knowledgeBase.analytics.viewAll}
          </Link>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-gray-200 p-0.5">
            <button
              onClick={() => setViewType('content')}
              className={cn(
                'rounded-md px-3 py-1 text-xs font-medium transition-colors',
                viewType === 'content'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-500 hover:text-gray-700',
              )}
            >
              {t.knowledgeBase.analytics.contentOverview}
            </button>
            <button
              onClick={() => setViewType('project')}
              className={cn(
                'rounded-md px-3 py-1 text-xs font-medium transition-colors',
                viewType === 'project'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-500 hover:text-gray-700',
              )}
            >
              {t.knowledgeBase.analytics.projectOverview}
            </button>
          </div>
          <select className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-600">
            <option>{t.knowledgeBase.analytics.timeRange.lastWeek}</option>
            <option>{t.knowledgeBase.analytics.timeRange.lastMonth}</option>
            <option>{t.knowledgeBase.analytics.timeRange.allTime}</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="p-4">
        {/* Created articles progress bar */}
        <div className="mb-4">
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="text-gray-600">{t.knowledgeBase.analytics.createdArticles}</span>
            <span className="font-medium text-gray-900">{formatNumber(totalArticles)}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-gray-100">
            <div className="flex h-full">
              <div
                className="bg-yellow-400"
                style={{
                  width: totalArticles > 0
                    ? `${(analytics?.createdArticles.draft ?? 0) / totalArticles * 100}%`
                    : '0%',
                }}
              />
              <div
                className="bg-green-500"
                style={{
                  width: totalArticles > 0
                    ? `${(analytics?.createdArticles.published ?? 0) / totalArticles * 100}%`
                    : '0%',
                }}
              />
            </div>
          </div>
          <div className="mt-1 flex gap-4 text-xs">
            <span className="flex items-center gap-1">
              <span className="h-2 w-2 rounded-full bg-yellow-400" />
              {t.knowledgeBase.analytics.draft} ({formatNumber(analytics?.createdArticles.draft ?? 0)})
            </span>
            <span className="flex items-center gap-1">
              <span className="h-2 w-2 rounded-full bg-green-500" />
              {t.knowledgeBase.analytics.published} ({formatNumber(analytics?.createdArticles.published ?? 0)})
            </span>
          </div>
        </div>

        {/* Stats grid */}
        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">{t.knowledgeBase.analytics.views}</span>
            <span className="font-medium text-gray-900">{formatNumber(analytics?.views ?? 0)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">{t.knowledgeBase.analytics.reads}</span>
            <span className="font-medium text-gray-900">{formatNumber(analytics?.reads ?? 0)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">{t.knowledgeBase.analytics.likes}</span>
            <span className="font-medium text-gray-900">{formatNumber(analytics?.likes ?? 0)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">{t.knowledgeBase.analytics.dislikes}</span>
            <span className="font-medium text-gray-900">{formatNumber(analytics?.dislikes ?? 0)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-1 text-gray-600">
              <AlertTriangle className="h-3 w-3 text-yellow-500" />
              {t.knowledgeBase.analytics.brokenLinks}
            </span>
            <span className="font-medium text-gray-900">{formatNumber(0)}</span>
          </div>
        </div>

        {/* Note */}
        <p className="mt-4 text-xs text-gray-400">
          {t.knowledgeBase.analytics.note}
        </p>
      </div>
    </div>
  );
}
